import React, { useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import toast from 'react-hot-toast';

// Simplified Icon Picker
function IconPicker({ onSelectIcon }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('farming');
  
  const categories = {
    farming: {
      label: '🚜 Farming',
      icons: ['🚜', '🌱', '🌾', '🌿', '🌻', '🌳', '🌵', '🌸', '🍃', '🌺']
    },
    vegetables: {
      label: '🥕 Vegetables',
      icons: ['🥕', '🥬', '🍅', '🌽', '🧅', '🧄', '🫑', '🥦', '🥒', '🌶️']
    },
    fruit: {
      label: '🍎 Fruit',
      icons: ['🍎', '🍓', '🍋', '🍊', '🍑', '🍒', '🍇', '🍉', '🥝', '🫐']
    },
    livestock: {
      label: '🐄 Livestock',
      icons: ['🐄', '🐔', '🐖', '🐑', '🐟', '🦃', '🐝', '🐏', '🦆', '🐕']
    },
    dairy_eggs: {
      label: '🧀 Dairy & Eggs',
      icons: ['🧀', '🥛', '🧈', '🍦', '🥚', '🍳', '🧇', '🥞']
    },
    bakery: {
      label: '🍞 Bakery',
      icons: ['🍞', '🥖', '🥨', '🥐', '🧁', '🍰', '🥮', '🍩']
    },
    transport: {
      label: '🚚 Transport',
      icons: ['🚚', '🚛', '🚗', '🚲', '✈️', '🚢', '🚌', '🛵', '🚐', '🏍️']
    },
    commerce: {
      label: '💰 Commerce',
      icons: ['💰', '🏪', '📦', '💳', '🪙', '📊', '💵', '🏷️', '🛒', '📈']
    },
    people: {
      label: '👥 People',
      icons: ['👥', '👤', '👨‍🌾', '👩‍🌾', '👪', '👶', '🧑‍🤝‍🧑', '👨‍👩‍👦', '👨‍🍳', '🧑‍🍳']
    },
    actions: {
      label: '✅ Actions',
      icons: ['✅', '❌', '⭐', '💡', '📤', '🔗', '📧', '🗳️', '🎯', '⚡']
    },
    misc: {
      label: '📌 Misc',
      icons: ['📌', '🎉', '⏰', '📱', '🌐', '🔔', '💬', '🎶', '📝', '🔒']
    }
  };

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="p-1.5 rounded hover:bg-gray-200 text-sm"
        title="Insert Icon"
      >
        😊
      </button>
      
      {isOpen && (
        <div 
          className="absolute left-0 top-full mt-1 w-72 max-h-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-wrap gap-0.5 p-1 border-b bg-gray-50 max-h-16 overflow-y-auto">
            {Object.keys(categories).map(cat => (
              <button
                type="button"
                key={cat}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveCategory(cat);
                }}
                className={`px-2 py-0.5 text-xs rounded whitespace-nowrap ${
                  activeCategory === cat 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {categories[cat].label}
              </button>
            ))}
          </div>
          <div className="p-2 overflow-y-auto max-h-48 grid grid-cols-6 gap-0.5">
            {categories[activeCategory].icons.map((emoji, index) => (
              <button
                type="button"
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectIcon(emoji);
                  setIsOpen(false);
                }}
                className="p-1 hover:bg-gray-100 rounded text-2xl text-center transition"
                title={emoji}
              >
                {emoji}
              </button>
            ))}
          </div>
          <div className="p-1 border-t bg-gray-50 text-[10px] text-gray-500 text-center">
            Select emoji • Use H1/H2/H3 to resize
          </div>
        </div>
      )}
    </div>
  );
}

function EditorToolbar({ editor, onImageUpload, onYouTubeEmbed, onVimeoEmbed, uploadingImage, onInsertIcon }) {
  if (!editor) return null;
  
  return (
    <div className="border rounded-t-lg p-2 bg-gray-50 flex flex-wrap gap-1">
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('bold') ? 'bg-gray-300' : ''}`}
        title="Bold"
      >
        <strong>B</strong>
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('italic') ? 'bg-gray-300' : ''}`}
        title="Italic"
      >
        <em>I</em>
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('strike') ? 'bg-gray-300' : ''}`}
        title="Strike"
      >
        <s>S</s>
      </button>
      
      <span className="w-px bg-gray-300 mx-1" />
      
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('heading', { level: 1 }) ? 'bg-gray-300' : ''}`}
        title="Heading 1"
      >
        H1
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('heading', { level: 2 }) ? 'bg-gray-300' : ''}`}
        title="Heading 2"
      >
        H2
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('heading', { level: 3 }) ? 'bg-gray-300' : ''}`}
        title="Heading 3"
      >
        H3
      </button>
      
      <span className="w-px bg-gray-300 mx-1" />
      
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('bulletList') ? 'bg-gray-300' : ''}`}
        title="Bullet List"
      >
        • List
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('orderedList') ? 'bg-gray-300' : ''}`}
        title="Numbered List"
      >
        1. List
      </button>
      
      <span className="w-px bg-gray-300 mx-1" />
      
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('blockquote') ? 'bg-gray-300' : ''}`}
        title="Quote"
      >
        " Quote
      </button>
      
      <span className="w-px bg-gray-300 mx-1" />
      
      <button
        type="button"
        onClick={() => {
          const url = prompt('Enter URL:');
          if (url) editor.chain().focus().setLink({ href: url }).run();
        }}
        className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('link') ? 'bg-gray-300' : ''}`}
        title="Link"
      >
        🔗
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().unsetLink().run()}
        className="p-1.5 rounded hover:bg-gray-200"
        title="Remove Link"
      >
        🔗✕
      </button>
      
      <span className="w-px bg-gray-300 mx-1" />
      
      <IconPicker onSelectIcon={onInsertIcon} />
      
      <span className="w-px bg-gray-300 mx-1" />
      
      <label
        className={`p-1.5 rounded hover:bg-gray-200 cursor-pointer ${uploadingImage ? 'opacity-50' : ''}`}
        title="Upload Image"
      >
        🖼️
        <input
          type="file"
          accept="image/*"
          onChange={onImageUpload}
          className="hidden"
          disabled={uploadingImage}
        />
        {uploadingImage && <span className="text-xs ml-1">Uploading...</span>}
      </label>

      <button
        type="button"
        onClick={() => {
          const url = prompt('Enter image URL:');
          if (url && editor) {
            editor.chain().focus().setImage({ src: url }).run();
          }
        }}
        className="p-1.5 rounded hover:bg-gray-200"
        title="Insert Image URL"
      >
        🖼️ URL
      </button>
      
      <span className="w-px bg-gray-300 mx-1" />
 
      <button
        type="button"
        onClick={onYouTubeEmbed}
        className="p-1.5 rounded hover:bg-gray-200"
        title="Embed YouTube Video"
      >
        ▶️ YouTube
      </button>
      <button
        type="button"
        onClick={onVimeoEmbed}
        className="p-1.5 rounded hover:bg-gray-200"
        title="Embed Vimeo Video"
      >
        🎬 Vimeo
      </button>
    </div>
  );
}

export default function RichTextEditor({ content, onChange, placeholder = 'Write your content here...' }) {
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        hardBreak: {
          keepMarks: true,
        },
      }),
      Image,
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  const insertIcon = (emoji) => {
    if (editor) {
      editor.chain().focus().insertContent(emoji).run();
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch('/api/upload/education', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        const imageUrl = data.url || data.imageUrl;
        
        if (editor && imageUrl) {
          editor.chain().focus().setImage({ src: imageUrl }).run();
          toast.success('Image uploaded successfully!');
        }
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleYouTubeEmbed = () => {
    const url = prompt('Enter YouTube URL:');
    if (!url || !editor) return;

    const patterns = [
      /(?:youtube\.com\/watch\?v=)([^&]+)/,
      /(?:youtu\.be\/)([^?]+)/,
      /(?:youtube\.com\/embed\/)([^?]+)/
    ];

    let videoId = null;
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        videoId = match[1];
        break;
      }
    }

    if (!videoId) {
      toast.error('Invalid YouTube URL');
      return;
    }

    const embedHtml = `<div class="video-embed"><iframe width="100%" height="400" src="https://www.youtube.com/embed/${videoId}" frameborder="0" allowfullscreen></iframe></div>`;
    editor.chain().focus().insertContent(embedHtml).run();
    toast.success('Video embedded successfully!');
  };

  const handleVimeoEmbed = () => {
    const url = prompt('Enter Vimeo URL:');
    if (!url || !editor) return;

    const match = url.match(/vimeo\.com\/(\d+)/);
    if (!match) {
      toast.error('Invalid Vimeo URL');
      return;
    }

    const videoId = match[1];
    const embedHtml = `<div class="video-embed"><iframe width="100%" height="400" src="https://player.vimeo.com/video/${videoId}" frameborder="0" allowfullscreen></iframe></div>`;
    editor.chain().focus().insertContent(embedHtml).run();
    toast.success('Video embedded successfully!');
  };

  return (
    <div 
      className="border rounded-lg"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <EditorToolbar 
        editor={editor} 
        onImageUpload={handleImageUpload}
        onYouTubeEmbed={handleYouTubeEmbed}
        onVimeoEmbed={handleVimeoEmbed}
        uploadingImage={uploadingImage}
        onInsertIcon={insertIcon}
      />
      <EditorContent 
        editor={editor} 
        className="prose max-w-none p-4 min-h-[150px] focus:outline-none"
      />
    </div>
  );
}
